import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { newsAPI } from '../../services/api';

const AdminNews = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'add'>('view');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchNews();
  }, [navigate]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAll();
      if (response.records) {
        setNews(response.records);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      // Use mock data in case of error
      setNews([
        {
          id: 1,
          title: 'Africa Surf Tour 2025 : Les Dates Révélées',
          title_fr: 'Africa Surf Tour 2025 : Les Dates Révélées',
          title_en: 'Africa Surf Tour 2025: Dates Revealed',
          content_fr: 'Le calendrier officiel de l\'Africa Surf Tour 2025 vient d\'être dévoilé...',
          content_en: 'The official calendar for the Africa Surf Tour 2025 has just been unveiled...',
          excerpt_fr: 'Découvrez le calendrier complet de la saison 2025 avec 6 étapes inédites à travers le continent africain.',
          excerpt_en: 'Discover the complete calendar for the 2025 season with 6 new stages across the African continent.',
          category: 'competitions',
          author: 'Équipe ASCsurf',
          image_url: 'https://images.pexels.com/photos/1430672/pexels-photo-1430672.jpeg?auto=compress&cs=tinysrgb&w=800',
          featured: true,
          tags: 'Africa Surf Tour,Compétitions,2025',
          read_time: 3,
          created_at: '2024-12-15T10:00:00Z'
        },
        {
          id: 2,
          title: 'Cherif Fall Qualifié pour les World Surf Games',
          title_fr: 'Cherif Fall Qualifié pour les World Surf Games',
          title_en: 'Cherif Fall Qualified for World Surf Games',
          content_fr: 'Une nouvelle historique pour le surf africain...',
          content_en: 'Historic news for African surfing...',
          excerpt_fr: 'Le champion sénégalais décroche sa qualification pour représenter l\'Afrique aux World Surf Games 2025.',
          excerpt_en: 'The Senegalese champion secures his qualification to represent Africa at the World Surf Games 2025.',
          category: 'athletes',
          author: 'Mohamed Kone',
          image_url: 'https://images.pexels.com/photos/1308881/pexels-photo-1308881.jpeg?auto=compress&cs=tinysrgb&w=800',
          featured: false,
          tags: 'Cherif Fall,World Surf Games,Sénégal',
          read_time: 4,
          created_at: '2024-12-10T14:30:00Z'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingNews(null);
    setViewMode('add');
    setShowModal(true);
  };

  const handleEdit = (newsItem: any) => {
    // Mapper les données pour correspondre aux champs du formulaire
    const mappedData = {
      ...newsItem,
      title_fr: newsItem.title_fr || newsItem.title || '',
      title_en: newsItem.title_en || newsItem.title || '',
      content_fr: newsItem.content_fr || newsItem.content || '',
      content_en: newsItem.content_en || newsItem.content || '',
      excerpt_fr: newsItem.excerpt_fr || newsItem.excerpt || '',
      excerpt_en: newsItem.excerpt_en || newsItem.excerpt || ''
    };
    setEditingNews(mappedData);
    setViewMode('edit');
    setShowModal(true);
  };

  const handleView = (newsItem: any) => {
    setEditingNews(newsItem);
    setViewMode('view');
    setShowModal(true);
  };

  const handleDelete = async (newsItem: any) => {
    try {
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'article "${newsItem.title}" ?`)) {
        await newsAPI.delete(newsItem.id);
        setNews(prev => prev.filter(item => item.id !== newsItem.id));
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Erreur lors de la suppression de l\'article');
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingNews) {
        // Update existing news
        const updatedData = { ...formData, id: editingNews.id };
        await newsAPI.update(updatedData);
        
        setNews(prev => prev.map(item => 
          item.id === editingNews.id ? { ...item, ...formData, title: formData.title_fr } : item
        ));
      } else {
        // Add new news
        const response = await newsAPI.create(formData);
        
        if (response.id) {
          const newNews = {
            id: response.id,
            ...formData,
            title: formData.title_fr,
            created_at: new Date().toISOString()
          };
          setNews(prev => [newNews, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingNews(null);
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Erreur lors de l\'enregistrement de l\'article');
      
      // Simulate successful save for demo purposes
      if (editingNews) {
        setNews(prev => prev.map(item => 
          item.id === editingNews.id ? { ...item, ...formData, title: formData.title_fr } : item
        ));
      } else {
        const newId = Math.max(0, ...news.map(item => item.id)) + 1;
        const newNews = {
          id: newId,
          ...formData,
          title: formData.title_fr,
          created_at: new Date().toISOString()
        };
        setNews(prev => [newNews, ...prev]);
      }
      
      setShowModal(false);
      setEditingNews(null);
    } finally {
      setFormLoading(false);
    }
  };

  const formFields = [
    {
      name: 'title_fr',
      label: 'Titre (Français)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre de l\'article en français'
    },
    {
      name: 'title_en',
      label: 'Titre (Anglais)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre de l\'article en anglais'
    },
    {
      name: 'excerpt_fr',
      label: 'Extrait (Français)',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Résumé de l\'article en français',
      rows: 3
    },
    {
      name: 'excerpt_en',
      label: 'Extrait (Anglais)',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Résumé de l\'article en anglais',
      rows: 3
    },
    {
      name: 'content_fr',
      label: 'Contenu (Français)',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Contenu complet de l\'article en français',
      rows: 8
    },
    {
      name: 'content_en',
      label: 'Contenu (Anglais)',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Contenu complet de l\'article en anglais',
      rows: 8
    },
    {
      name: 'category',
      label: 'Catégorie',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'competitions', label: 'Compétitions' },
        { value: 'athletes', label: 'Athlètes' },
        { value: 'development', label: 'Développement' },
        { value: 'partnerships', label: 'Partenariats' },
        { value: 'community', label: 'Communauté' },
        { value: 'environment', label: 'Environnement' }
      ]
    },
    {
      name: 'author',
      label: 'Auteur',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom de l\'auteur'
    },
    {
      name: 'image_url',
      label: 'URL de l\'image',
      type: 'text' as const,
      placeholder: 'https://example.com/image.jpg'
    },
    {
      name: 'tags',
      label: 'Tags',
      type: 'text' as const,
      placeholder: 'Tag1,Tag2,Tag3'
    },
    {
      name: 'read_time',
      label: 'Temps de lecture (minutes)',
      type: 'text' as const,
      placeholder: '5'
    },
    {
      name: 'featured',
      label: 'Article à la une',
      type: 'checkbox' as const
    }
  ];

  const columns = [
    {
      key: 'title',
      label: 'Titre',
      sortable: true,
      render: (value: string, row: any) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.author}</div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Catégorie',
      render: (value: string) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'featured',
      label: 'À la une',
      render: (value: boolean) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Oui' : 'Non'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des actualités...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Gestion des Actualités"
          data={news}
          columns={columns}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingNews(null);
          }}
          title={
            viewMode === 'add' 
              ? 'Nouvel article' 
              : viewMode === 'edit' 
                ? 'Modifier l\'article' 
                : 'Détails de l\'article'
          }
          size="lg"
        >
          {viewMode === 'view' ? (
            <div className="space-y-6">
              {editingNews?.image_url && (
                <div className="mb-6">
                  <img 
                    src={editingNews.image_url} 
                    alt={editingNews.title} 
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Titre (Français)</h3>
                <p className="text-gray-800">{editingNews?.title_fr}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Titre (Anglais)</h3>
                <p className="text-gray-800">{editingNews?.title_en}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Extrait (Français)</h3>
                <p className="text-gray-800">{editingNews?.excerpt_fr}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Extrait (Anglais)</h3>
                <p className="text-gray-800">{editingNews?.excerpt_en}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Contenu (Français)</h3>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-line">
                  {editingNews?.content_fr}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Contenu (Anglais)</h3>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-line">
                  {editingNews?.content_en}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Catégorie</h3>
                  <p className="text-gray-800">{editingNews?.category}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Auteur</h3>
                  <p className="text-gray-800">{editingNews?.author}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Tags</h3>
                  <p className="text-gray-800">{editingNews?.tags}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Temps de lecture</h3>
                  <p className="text-gray-800">{editingNews?.read_time} minutes</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">À la une</h3>
                <p className="text-gray-800">{editingNews?.featured ? 'Oui' : 'Non'}</p>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setViewMode('edit');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingNews(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <AdminForm
              fields={formFields}
              initialData={editingNews || {}}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowModal(false);
                setEditingNews(null);
              }}
              loading={formLoading}
            />
          )}
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminNews;